import React from "react";

const prefersReducedMotion = () => window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

//Wix-style parallax background, matched to the original site: the image is one window tall and moves at `speed` times the
//page's scroll speed (0.2 on the original), so it drifts slowly while the content scrolls past.
//Its top is kept at speed * (the section's distance from the top of the window).
//That always covers the visible part of the section, whatever the section's height. Uses transforms, so it also works on phones
export const useParallax = (ref, speed = 0.2) => {
  React.useEffect(() => {
    const img = ref.current;
    if (!img || prefersReducedMotion()) {
      return undefined;
    }

    let frame = null;
    const update = () => {
      frame = null;
      const rect = img.parentElement.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        return;
      }
      img.style.transform = `translate3d(0, ${(-(1 - speed) * rect.top).toFixed(1)}px, 0)`;
    };
    const requestUpdate = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [speed]);
};

//Full-width section with a parallax background photo. `position` is the image's alignment (e.g. "center top" keeps
//faces near the top in view); srcSet gives the browser sharper versions for wide / high-resolution screens
const Parallax = ({ src, srcSet = null, alt = "", className = "", speed = 0.2, position = "center", as: Tag = "div", children = null, ...props }) => {
  const imgRef = React.useRef(null);
  useParallax(imgRef, speed);

  return (
    <Tag
      className={`parallax ${className}`}
      {...props}
    >
      <img
        ref={imgRef}
        src={src}
        srcSet={srcSet || undefined}
        sizes={srcSet ? "100vw" : undefined}
        alt={alt}
        className={"parallax-img"}
        style={{ objectPosition: position }}
        decoding={"async"}
      />
      {children}
    </Tag>
  );
};

export default Parallax;
